import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toApiError, useApi } from '@/shared/api';
import { workspaceKeys } from './queries';
import { resendFailureText } from './workspaceAdminPresentation';

export type ResendState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'sent' }
  | { status: 'failed'; reason: string };

export interface AdminInvitationResend {
  stateOf: (userId: string) => ResendState;
  resend: (workspaceId: string, userId: string) => void;
}

const IDLE: ResendState = { status: 'idle' };

/**
 * „Einladung erneut senden" je Administrator. Der Zustand hängt am Administrator, nicht an der
 * Mutation: zwei Einladungen kurz nacheinander überschreiben einander nicht.
 */
export function useAdminInvitationResend(): AdminInvitationResend {
  const api = useApi();
  const queryClient = useQueryClient();
  const [states, setStates] = useState<Record<string, ResendState>>({});
  const set = (userId: string, state: ResendState) =>
    setStates((current) => ({ ...current, [userId]: state }));

  const mutation = useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: string; userId: string }) =>
      api.workspaces.resendAdminInvitation(workspaceId, userId),
    // Ohne „always" pausiert TanStack offline still, und der Knopf dreht sich ohne Ende.
    networkMode: 'always',
    onMutate: ({ userId }) => set(userId, { status: 'sending' }),
    onSuccess: (_, { userId }) => set(userId, { status: 'sent' }),
    onError: async (error, { userId }) => {
      set(userId, { status: 'failed', reason: resendFailureText(await toApiError(error)) });
      // Meist ist die Liste veraltet (angenommen, gesperrt, entfernt): neu laden zeigt den Stand.
      await queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
    },
  });

  return {
    stateOf: (userId) => states[userId] ?? IDLE,
    resend: (workspaceId, userId) => mutation.mutate({ workspaceId, userId }),
  };
}
